import { useCallback, useMemo, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import dayjs from "dayjs";

import { AddOnType } from "app/contexts/AddonStateContext/const";
import { useAddonsState } from "app/contexts/AddonStateContext/AddonStateContext";
import type { ProjectsCtaBannerType } from "app/pages/activation/components/AddonBanners/ProjectsCtaBanner/TopBannerType";
import { ProjectsCtaBannerDict } from "app/pages/activation/components/AddonBanners/ProjectsCtaBanner/TopBannerType";
import { useHomeContext } from "app/pages/activation/context/Home/HomeContext";
import { ActivationComponentAssets } from "app/pages/activation/helpers/ActivationAssets";
import { useLocalStorage } from "app/pages/activation/hooks/helpers/useLocalStorage";

import { LOCAL_STORAGE_KEY, DAY_TIMESTAMP } from "../constants";

const DAYS_OFFSET = 1;

const getCurrentTimestamp = () => dayjs().unix();

const getOffsetTimestamp = (offset: number) => offset * DAY_TIMESTAMP;

interface IUseProjectsCtaBannerApi {
    bannerType: ProjectsCtaBannerType;
    hasProjectsCtaBanner: boolean;
    onClose: () => void;
}

export const useProjectsCtaBanner = (): IUseProjectsCtaBannerApi => {
    const {
        state: { parent },
    } = useCurrentStateAndParams();
    const { totalNumber: totalNumberOfProjects } = useHomeContext();
    const maxProjects = ActivationComponentAssets.activationSetsLimitation;

    const hasProjects = totalNumberOfProjects !== 0 && totalNumberOfProjects !== null;
    const hasProjectsAddon = maxProjects !== 1;

    const [isOpen, setIsOpen] = useState(true);

    const { getLocalStorage, setLocalStorage } = useLocalStorage();
    const { hasAddonsHub, getIsAddonAvailable } = useAddonsState();

    const canShowUsersBanner = getIsAddonAvailable(AddOnType.AdditionalUsers);

    const canShowProjectBanner = useMemo(
        () => !hasProjectsAddon && getIsAddonAvailable(AddOnType.AdditionalProjects),
        [getIsAddonAvailable, hasProjectsAddon],
    );

    const onClose = useCallback(() => {
        setIsOpen(false);
        setLocalStorage(LOCAL_STORAGE_KEY, getCurrentTimestamp());
    }, [setLocalStorage]);

    const shouldProjectsCtaBannerBeVisibleAgain =
        +getLocalStorage(LOCAL_STORAGE_KEY, 0) + getOffsetTimestamp(DAYS_OFFSET) <= getCurrentTimestamp();

    const bannerType = useMemo(() => {
        if (!isOpen || !hasAddonsHub || !shouldProjectsCtaBannerBeVisibleAgain) {
            return ProjectsCtaBannerDict.None;
        }

        if (hasProjects && canShowProjectBanner) {
            return ProjectsCtaBannerDict.ProjectsCta;
        }

        if (canShowUsersBanner) {
            return ProjectsCtaBannerDict.UsersCta;
        }

        return ProjectsCtaBannerDict.None;
    }, [
        canShowProjectBanner,
        canShowUsersBanner,
        hasAddonsHub,
        hasProjects,
        isOpen,
        shouldProjectsCtaBannerBeVisibleAgain,
    ]);

    const hasProjectsCtaBanner = useMemo(
        () => bannerType !== ProjectsCtaBannerDict.None && parent === "activation",
        [bannerType, parent],
    );

    return { bannerType, hasProjectsCtaBanner, onClose };
};
